import React, { useEffect, useLayoutEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Hero from './components/Hero';
import Demo from './components/Demo';
import Features from './components/Features';
import LanguagesSection from './components/LanguagesSection';
import Languages from './components/Languages';
import LanguageLandingPage from './components/LanguageLandingPage';
import CTA from './components/CTA';
import Footer from './components/Footer';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import BugReport from './components/BugReport';
import CookieConsent from './components/CookieConsent';
import Success from './components/Success';
import SuccessV2 from './components/SuccessV2';
import NotFound from './components/NotFound';
import Checkout from './components/Checkout';
import Tutorial from './components/Tutorial';
import CookiePreferences from './components/CookiePreferences';
import BlogIndex from './components/blog/BlogIndex';
import BlogPost from './components/blog/BlogPost';
import PageMeta from './components/seo/PageMeta';
import { LanguageLandingProvider } from './context/LanguageLandingContext';
import {
  DEFAULT_DOCUMENT_TITLE,
  DEFAULT_META_DESCRIPTION,
} from './constants/seoDefaults';
import posthog from 'posthog-js';

// Scroll to top on route change; nudge layout so Safari runs IntersectionObserver
// for Framer `whileInView` right after client-side navigation (avoids "invisible until scroll").
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void document.documentElement.offsetHeight;
        window.scrollTo(0, 0);
      });
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return null;
}

function HomePage() {
  return (
    <LanguageLandingProvider language={null}>
      <PageMeta
        title={DEFAULT_DOCUMENT_TITLE}
        description={DEFAULT_META_DESCRIPTION}
        canonicalPath="/"
        keywords="Premiere Pro subtitles, animated captions, Ukrainian subtitles Premiere Pro, multilingual captions, Substyle"
        htmlLang="en"
      />
      <Hero />
      <Demo />
      <Features />
      <LanguagesSection />
      <CTA />
    </LanguageLandingProvider>
  );
}

function PricingPage() {
  return <Pricing />;
}

function LanguagesPage() {
  return <Languages />;
}

function LanguageLandingRoute() {
  return <LanguageLandingPage />;
}

function FAQPage() {
  return <FAQ />;
}

function BugReportPage() {
  return <BugReport />;
}

function SuccessPage() {
  return <Success />;
}

function SuccessV2Page() {
  return <SuccessV2 />;
}

function CheckoutPage() {
  return <Checkout />;
}

function TutorialPage() {
  return <Tutorial />;
}

function NotFoundPage() {
  return <NotFound />;
}

function CookiePreferencesPage() {
  return <CookiePreferences />;
}

function BlogIndexPage() {
  return <BlogIndex />;
}

function BlogPostPage() {
  return <BlogPost />;
}

function App() {
  const { pathname } = useLocation();
  const isCheckoutPage = pathname === '/checkout';

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      funnel_step: 'site_visit',
    });
  }, [pathname]);

  return (
    <div className="App">
      <ScrollToTop />
      {!isCheckoutPage && <Header />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/languages" element={<LanguagesPage />} />
        <Route path="/languages/:slug" element={<LanguageLandingRoute />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/bugreport" element={<BugReportPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/v2/success" element={<SuccessV2Page />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/from-extension" element={<Navigate to="/" replace />} />
        <Route path="/tutorial" element={<TutorialPage />} />
        <Route path="/cookie-preferences" element={<CookiePreferencesPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!isCheckoutPage && <Footer />}
      {!isCheckoutPage && <CookieConsent />}
    </div>
  );
}

function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;
